import {changeDoseUnitLabel} from '@/modules/protocols/functions'
import {DoseCalcModeActive, OrderBatchCategory, PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientTherapySequence, PatientTherapySequenceMedicine} from '@cypro/engine/schema'
import {therapySequenceIncludesActiveSubstance} from '@cypro/engine/utils'
import {
  extractTimestamp,
  getDatetimeFormatterUTC,
  getNumberFormatter,
  multipleDigitsNumberFormatter,
  isDefined,
} from '@cypro/ui/utils'
import {TFnType} from '@tolgee/react'
import {startOfToday} from 'date-fns'
import {type Action} from './hooks/use-therapy-sequence-actions'

/**
 * Lot numbers of the drug batches this prescription used. Carrier and solvent lots live on the same
 * relation but are never mixed into this field (CY-382).
 */
export function getSequenceDrugLotNumbers(
  sequence: Pick<PatientTherapySequence, 'batches'> | null | undefined
): string[] {
  return (sequence?.batches ?? [])
    .filter(batch => batch.category === OrderBatchCategory.enum.ACTIVE_SUBSTANCE)
    .map(batch => batch.batch?.lotNumber)
    .filter((lotNumber): lotNumber is string => Boolean(lotNumber))
}

export function getSequencePatientSummary(sequence: PatientTherapySequence) {
  if (!sequence) return ''

  const patient = sequence?.patient
  const protocol = sequence?.protocol

  const details: string[] = []
  if (patient?.gender) details.push(patient.gender.charAt(0).toUpperCase())
  if (isDefined(patient?.birthDate)) details.push(new Date(patient.birthDate).toISOString().split('T')[0])
  if (protocol?.diagnosis?.name) details.push(protocol.diagnosis.name)

  return details.join(' • ')
}

function getSequenceStandardizedMedicine(medicine: PatientTherapySequenceMedicine) {
  const unit =
    medicine.activeSubstance?.activeSubstanceDetails?.doseUnit ||
    medicine.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.doseUnit

  return {
    ...medicine,
    name: medicine.activeSubstance?.name || medicine.prescribedName?.name,
    unit,
  }
}

export function getSequenceStandardizedMedicines(sequence: PatientTherapySequence) {
  return sequence.medicine?.map(getSequenceStandardizedMedicine) || []
}

export function getSequenceActiveSubstancePreparationType(sequence: PatientTherapySequence) {
  const medicine = sequence.medicine?.find(
    m =>
      m.activeSubstance?.activeSubstanceDetails?.preparationType ||
      m.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.preparationType
  )

  return (
    medicine?.activeSubstance?.activeSubstanceDetails?.preparationType ||
    medicine?.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.preparationType
  )
}

export function groupSequencesByDate(sequences: readonly PatientTherapySequence[] | undefined) {
  return Object.groupBy(sequences || [], item => extractTimestamp(item.therapyDate))
}

// therapyDate is a midnight timestamp and time is 'HH:MM' — this builds the full timestamp
export function getSequenceTimestamp(therapy: {therapyDate: number; time: string}) {
  const [hour, minute] = therapy.time.split(':').map(Number)
  return therapy.therapyDate + (hour * 60 + minute) * 60_000
}

// the scheduled slot is stored as UTC wall-clock, so it must be rendered in UTC
export function formatSequenceDatetime(therapy: {therapyDate: number; time: string}) {
  return getDatetimeFormatterUTC().format(new Date(getSequenceTimestamp(therapy)))
}

// Translation key for the status badge; the validating phase splits into two sub-steps.
export type SequenceStatusLabelKey =
  PatientTherapySequenceStatus | 'validating.pharmacistCheck' | 'validating.patientArrival'

export function getSequenceStatusLabelKey(
  sequence: Pick<PatientTherapySequence, 'status' | 'pharmacistValidatedAt' | 'patientArrivedAt'> | undefined | null,
  patientArrivalEnabled = true
): SequenceStatusLabelKey | null {
  if (!sequence?.status) return null

  if (sequence.status === PatientTherapySequenceStatus.enum.validating) {
    if (!sequence.pharmacistValidatedAt) return 'validating.pharmacistCheck'
    // the arrival sub-step only exists while the doctorRelease setting keeps it enabled
    if (patientArrivalEnabled && !sequence.patientArrivedAt) return 'validating.patientArrival'
  }

  return sequence.status
}

export function isSequencePassed(sequence: PatientTherapySequence, reference = startOfToday()) {
  return getSequenceTimestamp(sequence) < reference.getTime()
}

export function sequenceIncludesActiveSubstance(sequence: PatientTherapySequence | undefined) {
  return therapySequenceIncludesActiveSubstance(sequence)
}

// statuses where the unit is produced and handed over (production number / use-by apply)
export {isPostProduction} from '@cypro/engine/utils'

export function formatTreatmentLabels(
  sequence: PatientTherapySequence | undefined | null,
  t: TFnType,
  useActiveSubstance = false
) {
  const medicines = sequence?.medicine || []

  const names = medicines.map(m =>
    useActiveSubstance ? m.activeSubstance?.name : m.prescribedName?.name || m.activeSubstance?.name
  )

  const dosages = medicines.map(m => {
    const labels: (string | number)[] = []
    if (m.totalAmount) labels.push(multipleDigitsNumberFormatter.format(m.totalAmount))
    const unit = m.activeSubstance?.activeSubstanceDetails?.doseUnit
    if (unit) labels.push(unit.ucumCode)
    return labels.join(' ')
  })

  const meds: {name: string; dosage: string}[] = []
  for (let i = 0; i < names.length; i++) {
    const name = names[i]
    const dosage = dosages[i]
    if (name && dosage) meds.push({name, dosage})
  }

  const carrier = sequence?.carrierSolution
  const carrierDetails = []
  if (sequence?.volumeMode) carrierDetails.push(sequence?.volumeMode)
  if (carrier?.name) carrierDetails.push(carrier?.name)
  if (sequence?.volume) carrierDetails.push(`(${t('x_ml', {value: sequence?.volume})})`)

  return {names, dosages, carrierDetails, medicines: meds}
}

// actions whose irreversible outcome warrants the danger palette in the UI
export function isDestructiveAction(action: Action) {
  return action === 'RETURN_TO_PHARMACY' || action === 'SPOILAGE'
}

// every medicine must have a prescription trade name selected before quick validation
export function isMissingPrescribedName(
  medicine: readonly {prescribedNameId?: string | null | undefined}[] | undefined
) {
  return Boolean(medicine?.some(m => !m.prescribedNameId))
}

// AUC-based dosing needs the patient's GFR; without it the dose math returns 0,
// so quick validation must be blocked and the pharmacist must use the full review
export function isAucWithoutGfr(
  medicine: readonly {calculationMethod?: string | null | undefined}[] | undefined,
  gfr?: number | null
) {
  return Boolean(medicine?.some(m => m.calculationMethod === 'AUC') && !gfr)
}

export function getSequenceMedicineNumberFormatter(medicine: PatientTherapySequenceMedicine | undefined | null) {
  return getNumberFormatter({
    maximumFractionDigits: Number(medicine?.precisionDecimals ?? 0),
  })
}

// e.g. "AUC 5 (mg * min/ml)" — ABSOLUTE doses carry no mode suffix since the amount
// already shows in Substance \ Dose; dosage for calculation-based modes is the
// regular dose entered in the mode's unit
export function getMedicineCalculationModeLabel(t: TFnType, medicine: PatientTherapySequenceMedicine) {
  const method = medicine.calculationMethod
  if (!method) return t('ABSOLUTE')

  if (method === DoseCalcModeActive.enum.ABSOLUTE) return t(method)

  const doseUnit = changeDoseUnitLabel(
    method,
    medicine.activeSubstance,
    medicine.activeSubstance?.activeSubstanceDetails?.doseUnit?.displayName
  )

  return `${t(method)} ${multipleDigitsNumberFormatter.format(medicine.regularDose ?? 0)} (${doseUnit})`
}
